// Écran 8 — Interroger : une question libre sur la carte. Le modèle ne répond pas : la console traduit la question en
// opérations de lecture, le code les exécute et rédige des énoncés qui renvoient chacun à une source citée.
//
// Règles d'affichage (docs/CONTRAT.md, « Console d'interrogation », ligne « Application web »; relecture Q12) :
// - énoncés, synthèse, manques et sources en TEXTE BRUT (jamais de HTML, de Markdown ni de lien automatique) ;
// - un nom de la carte est toujours rendu comme un nom (entre « », style distinct), jamais fondu dans le texte ;
// - seuls liens : vers la carte, par lienVers('carte', { objet }) ; aucune adresse tirée de la carte ;
// - la question ne va ni dans l'URL, ni dans le titre de la page, ni dans un stockage du navigateur, et n'est jamais
//   posée depuis un lien (cet écran ne lit aucun paramètre de l'adresse) ;
// - l'historique n'existe que dans la mémoire de l'onglet (domaine/console.ts).
import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type FormEvent, type KeyboardEvent } from 'react';
import type { ChiffreConsole, ManqueConsole, MorceauConsole, OperationConsole, PhraseConsole, ReponseConsole, SourceConsole, TypeObjet } from '../api/types';
import { Citations, Erreur, Etiquette } from '../composants/communs';
import { useEkera } from '../contexte';
import {
  ajouterHistorique,
  chiffresDePhrase,
  decouperSynthese,
  genre,
  LIBELLE_MANQUE,
  LIBELLE_ORIGINE,
  LIBELLE_RAISON_REFUS,
  LIBELLE_STATUT_REPONSE,
  ligneTexte,
  LONGUEUR_QUESTION,
  lireHistorique,
  majuscule,
  memeTexte,
  phrasesDuMessage,
  QUESTIONS_TYPES,
  recetteCalcul,
  sourceDuCalcul,
  surHistorique,
  texteCopie,
  versionHistorique,
} from '../domaine/console';
import { LIBELLE_STATUT_FAIT, LIBELLE_TYPE, pourcent } from '../domaine/format';
import { lienVers } from '../routeur';
import { LienRetour } from '../composants/LienRetour';

const NBSP = String.fromCharCode(160);

export function EcranInterroger() {
  const { client, cid } = useEkera();
  const [question, setQuestion] = useState('');
  const [sansModele, setSansModele] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [selection, setSelection] = useState<number | null>(null);
  const vivant = useRef(true);
  const titreReponse = useRef<HTMLHeadingElement>(null);
  const idAide = useId();
  useEffect(() => {
    vivant.current = true;
    return () => {
      vivant.current = false;
    };
  }, []);
  const v = useSyncExternalStore(surHistorique, versionHistorique);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const historique = useMemo(() => lireHistorique(cid), [cid, v]);
  const courante = historique.find((e) => e.n === selection) ?? historique[0] ?? null;

  async function poser() {
    const q = question.trim();
    if (!q || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      const r = await client.poserQuestion(cid, { question: q, mode: sansModele ? 'regles' : 'auto' });
      const e = ajouterHistorique(cid, q, r);
      if (vivant.current) {
        setSelection(e.n);
        // La réponse arrive : le focus va à son titre (lecteurs d'écran, navigation au clavier).
        setTimeout(() => titreReponse.current?.focus(), 0);
      }
    } catch (err) {
      if (vivant.current) setErreur(err);
    } finally {
      if (vivant.current) setEnCours(false);
    }
  }
  const envoyer = (e: FormEvent) => {
    e.preventDefault(); // jamais de navigation : la question ne doit pas atterrir dans l'adresse
    void poser();
  };
  const raccourci = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void poser();
    }
  };

  return (
    <div className="pile">
      <p className="sous-titre">
        Posez une question en français sur la carte. La console ne répond qu’à partir de ce que la carte contient et de ce que le
        moteur calcule : chaque phrase renvoie à ce qui la fonde, chaque chiffre à son calcul. Elle lit la carte, elle ne la
        modifie jamais.
      </p>

      <LienRetour depuis="console" />
      <section className="panneau" aria-labelledby="t-question">
        <div className="panneau-tete">
          <h2 id="t-question">Votre question</h2>
          <span className="indice">Une question à la fois, sans lien avec la précédente</span>
        </div>
        <form onSubmit={envoyer} className="pile" autoComplete="off" data-testid="formulaire-question">
          <div className="champ">
            <label htmlFor="question">Question sur la carte</label>
            <textarea
              id="question"
              rows={3}
              maxLength={LONGUEUR_QUESTION}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={raccourci}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              aria-describedby={idAide}
              placeholder={`Par exemple : ${QUESTIONS_TYPES[0] ?? ''}`}
            />
            <span className="aide" id={idAide}>
              <span className="num" data-testid="compteur-question">
                {question.length} / {LONGUEUR_QUESTION}
              </span>{' '}
              caractères. Ni l’adresse de la page ni le navigateur ne gardent la question ; elle est consignée dans le journal chez
              votre entreprise, avec votre nom et la réponse montrée. Ctrl + Entrée pour la poser.
            </span>
          </div>
          <label className="case">
            <input type="checkbox" checked={sansModele} onChange={(e) => setSansModele(e.target.checked)} />
            Répondre sans modèle de langage (questions types seulement)
          </label>
          <div className="ligne">
            <button type="submit" className="btn principal" disabled={enCours || question.trim() === ''}>
              {enCours ? 'La console lit la carte…' : 'Poser la question'}
            </button>
            {enCours && (
              <span className="note" role="status">
                Lecture de la carte, calcul, puis rédaction : quelques secondes.
              </span>
            )}
          </div>
        </form>
        <div className="pile" style={{ gap: 6 }}>
          <p className="note" id="questions-types">
            Questions types (elles remplissent le champ, sans rien poser) :
          </p>
          <ul className="liste-types" aria-labelledby="questions-types">
            {QUESTIONS_TYPES.map((q) => (
              <li key={q}>
                <button type="button" className="lien-btn" onClick={() => setQuestion(q)}>
                  {q}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {erreur != null && <Erreur erreur={erreur} titre="La console n’a pas pu répondre." />}

      {courante ? (
        <VueReponse key={courante.n} question={courante.question} reponse={courante.reponse} titreRef={titreReponse} />
      ) : (
        <p className="note" data-testid="aucune-reponse">
          Aucune question posée dans cette session.
        </p>
      )}

      {historique.length > 0 && (
        <section className="panneau" aria-labelledby="t-historique">
          <div className="panneau-tete">
            <h2 id="t-historique">Questions de cette session</h2>
            <span className="indice">Gardées dans la mémoire de cet onglet seulement : elles disparaissent à la fermeture</span>
          </div>
          <ol className="historique" data-testid="historique">
            {historique.map((e) => (
              <li key={e.n}>
                <button type="button" className="lien-btn" aria-current={e.n === courante?.n ? 'true' : undefined} onClick={() => setSelection(e.n)}>
                  {abreger(e.question)}
                </button>{' '}
                <Etiquette ton={e.reponse.statut === 'repondu' ? 'ok' : e.reponse.statut === 'partiel' ? 'attention' : 'neutre'}>
                  {e.reponse.statut === 'repondu' ? 'Répondu' : e.reponse.statut === 'partiel' ? 'Partiel' : 'Refusé'}
                </Etiquette>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

function abreger(q: string): string {
  const un = q.replace(/\s+/g, ' ');
  return un.length > 100 ? `${un.slice(0, 99)}…` : un;
}

// ---------------------------------------------------------------- une réponse

export function VueReponse({
  question,
  reponse,
  titreRef,
}: {
  question: string;
  reponse: ReponseConsole;
  titreRef?: React.RefObject<HTMLHeadingElement | null>;
}) {
  const chiffres = useMemo(() => new Map(reponse.chiffres.map((c) => [c.id, c])), [reponse.chiffres]);
  const sources = useMemo(() => new Map(reponse.sources.map((s) => [s.id, s])), [reponse.sources]);
  const [copie, setCopie] = useState<'' | 'ok' | 'echec'>('');
  const manques = reponse.manques.filter((m) => m.texte);
  const tonStatut = reponse.statut === 'repondu' ? 'ok' : reponse.statut === 'partiel' ? 'attention' : 'neutre';

  async function copier() {
    try {
      await navigator.clipboard.writeText(texteCopie(reponse));
      setCopie('ok');
    } catch {
      setCopie('echec');
    }
  }

  return (
    <section className="panneau" aria-labelledby="t-reponse" data-testid="reponse" data-statut={reponse.statut}>
      <div className="panneau-tete">
        <h2 id="t-reponse" ref={titreRef} tabIndex={-1}>
          Réponse
        </h2>
        <span className="ligne">
          <Etiquette ton={tonStatut}>{LIBELLE_STATUT_REPONSE[reponse.statut] ?? reponse.statut}</Etiquette>
          <Etiquette ton="neutre">{reponse.mode === 'modele' ? 'Rédigée avec un modèle de langage' : 'Sans modèle de langage'}</Etiquette>
        </span>
      </div>
      <p className="question-posee">
        <span className="note">Votre question :</span> <span data-testid="question-posee">{question}</span>
      </p>

      {reponse.statut === 'refuse' && <Refus reponse={reponse} />}

      {reponse.phrases.length > 0 && (
        <div className="pile" data-testid="phrases">
          {reponse.phrases.map((p, i) => (
            <Phrase key={i} numero={i} phrase={p} chiffres={chiffres} sources={sources} operations={reponse.operations} />
          ))}
        </div>
      )}

      {reponse.synthese.length > 0 && (
        <div className="synthese" data-testid="synthese">
          <h3>Synthèse</h3>
          <p className="note">Écrite par la console d’après le calcul, jamais par le modèle ; elle ne peut pas être omise.</p>
          <ul className="liste-points">
            {reponse.synthese.map((l, i) => (
              <LigneSynthese key={i} morceaux={l.morceaux} chiffres={chiffres} />
            ))}
          </ul>
        </div>
      )}

      {manques.length > 0 && (
        <div className="encadre attention" data-testid="manques">
          <p>
            <b>Ce que la carte ne permet pas de dire</b>
          </p>
          <ul className="liste-simple">
            {manques.map((m, i) => (
              <ManqueLigne key={i} manque={m} chiffres={chiffres} />
            ))}
          </ul>
        </div>
      )}

      <details>
        <summary>Ce que la console a lu et calculé ({libelleOperations(reponse.operations.length)})</summary>
        <div className="pile" style={{ marginTop: 8 }}>
          {reponse.operations.map((o) => (
            <Operation key={o.id} op={o} />
          ))}
          <p className="note">
            Carte « {reponse.carte} », révision {reponse.revision}, empreinte <span className="num">{reponse.empreinte}</span>
            {reponse.usage?.duree_ms ? ` · ${reponse.usage.duree_ms} ms` : ''}.
          </p>
        </div>
      </details>

      <div className="ligne">
        <button type="button" className="btn petit" onClick={() => void copier()}>
          Copier la réponse en texte
        </button>
        {copie === 'ok' && (
          <span className="note" role="status">
            Réponse copiée (sans la question ; balisage neutralisé).
          </span>
        )}
        {copie === 'echec' && (
          <span className="note" role="alert">
            Le navigateur a refusé la copie.
          </span>
        )}
      </div>
    </section>
  );
}

const libelleOperations = (n: number) => (n === 0 ? 'aucune opération' : n === 1 ? '1 opération' : `${n} opérations`);

/**
 * Le refus : le message écrit par la console (il dit la borne d'une durée ou d'un nombre de scénarios hors limites), sa
 * première phrase en avant, puis la raison du refus, sauf quand elle répète le message mot pour mot.
 */
function Refus({ reponse }: { reponse: ReponseConsole }) {
  const message = reponse.message ?? 'Je ne peux pas l’établir à partir de la carte.';
  const { premiere, suite } = phrasesDuMessage(message);
  const raison = reponse.refus ? (LIBELLE_RAISON_REFUS[reponse.refus.raison] ?? 'La console n’a pas répondu.') : null;
  return (
    <div className="encadre info" role="note" data-testid="refus">
      <p>
        <b>{premiere}</b>
      </p>
      {suite && <p>{suite}</p>}
      {raison && !memeTexte(raison, message) && <p>{raison}</p>}
    </div>
  );
}

/**
 * Une ligne de synthèse : le libellé de l'opération en titre, puis un élément par ligne (critères, résultats, chiffre
 * principal, nature des chiffres, hypothèses, manques). Les mots sont ceux du code ; seule la mise en page change, pour que
 * la nature des chiffres (règle éditoriale) ne se perde pas au milieu d'une ligne de plusieurs centaines de caractères.
 */
function LigneSynthese({ morceaux, chiffres }: { morceaux: readonly MorceauConsole[]; chiffres: Map<string, ChiffreConsole> }) {
  const { titre, elements } = decouperSynthese(morceaux);
  return (
    <li className="ok" data-testid="ligne-synthese">
      {titre === null ? (
        <Morceaux morceaux={elements[0] ?? []} chiffres={chiffres} />
      ) : (
        <>
          <p className="synthese-titre">{titre}</p>
          <ul className="synthese-elements">
            {elements.map((e, i) => (
              <li key={i} data-testid="element-synthese">
                <Morceaux morceaux={e} chiffres={chiffres} />
              </li>
            ))}
          </ul>
        </>
      )}
    </li>
  );
}

function ManqueLigne({ manque, chiffres }: { manque: ManqueConsole; chiffres: Map<string, ChiffreConsole> }) {
  return (
    <li>
      <Etiquette ton={manque.obligatoire === false ? 'neutre' : 'attention'}>{LIBELLE_MANQUE[manque.code] ?? 'À noter'}</Etiquette>{' '}
      {manque.morceaux && manque.morceaux.length > 0 ? <Morceaux morceaux={manque.morceaux} chiffres={chiffres} /> : manque.texte}
    </li>
  );
}

// ---------------------------------------------------------------- une phrase et ses chiffres

function Phrase({
  numero,
  phrase,
  chiffres,
  sources,
  operations,
}: {
  numero: number;
  phrase: PhraseConsole;
  chiffres: Map<string, ChiffreConsole>;
  sources: Map<string, SourceConsole>;
  operations: OperationConsole[];
}) {
  const [ouverts, setOuverts] = useState<ReadonlySet<string>>(new Set());
  const base = useId();
  const basculer = (id: string) =>
    setOuverts((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const cites = chiffresDePhrase(phrase.elements)
    .map((id) => chiffres.get(id))
    .filter((c): c is ChiffreConsole => !!c);
  const sourcesPhrase = (phrase.sources ?? []).map((id) => sources.get(id)).filter((s): s is SourceConsole => !!s);
  return (
    <div className="phrase" data-testid={`phrase-${numero}`}>
      <p className="enonce">
        {phrase.elements.map((el, i) =>
          'e' in el ? (
            <Morceaux key={i} morceaux={el.morceaux} chiffres={chiffres} ouverts={ouverts} basculer={basculer} base={base} />
          ) : (
            <Liaison key={i} t={el.t} dernier={i === phrase.elements.length - 1} />
          ),
        )}
      </p>
      {cites.map((c) =>
        ouverts.has(c.id) ? (
          <CalculChiffre key={c.id} id={`${base}-${c.id}`} chiffre={c} operations={operations} sources={[...sources.values()]} surFermer={() => basculer(c.id)} />
        ) : null,
      )}
      {sourcesPhrase.length > 0 && (
        <details className="sources-phrase">
          <summary>
            Ce qui fonde cette phrase ({sourcesPhrase.length} source{sourcesPhrase.length > 1 ? 's' : ''})
          </summary>
          <div className="pile" style={{ marginTop: 8 }}>
            {sourcesPhrase.map((s) => (
              <SourceBloc key={s.id} source={s} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function Liaison({ t, dernier }: { t: string; dernier: boolean }) {
  if (t === 'et') return <span> et </span>;
  if (t === ';') return <span>{NBSP}; </span>;
  if (t === ',') return <span>, </span>;
  if (t === '.') return <span>{dernier ? '.' : '. '}</span>;
  return null;
}

/**
 * Les morceaux d'un énoncé, en texte brut. Rien n'est interprété : un nom ou une valeur de la carte reste du texte, entre
 * « », dans un style à part ; seul un objet de la carte est un lien, et il mène à la carte.
 */
export function Morceaux({
  morceaux,
  chiffres,
  ouverts,
  basculer,
  base,
}: {
  morceaux: readonly MorceauConsole[];
  chiffres: Map<string, ChiffreConsole>;
  ouverts?: ReadonlySet<string>;
  basculer?: (id: string) => void;
  base?: string;
}) {
  return (
    <>
      {morceaux.map((m, i) => {
        switch (genre(m)) {
          case 'texte':
            return <span key={i}>{(m as { t: string }).t}</span>;
          case 'objet': {
            const o = m as { o: string; nom: string; type?: string | null };
            const type = o.type && o.type in LIBELLE_TYPE ? LIBELLE_TYPE[o.type as TypeObjet] : undefined;
            return (
              <a key={i} className="nom-carte lien" href={lienVers('carte', { objet: o.o })} title={type ? `${type} de la carte : voir la fiche` : 'Voir la fiche dans la carte'}>
                «{NBSP}{o.nom}{NBSP}»
              </a>
            );
          }
          case 'attribut': {
            const a = m as { libelle: string; valeur: string };
            return (
              <span key={i}>
                {a.libelle}
                {NBSP}: <span className="nom-carte">{a.valeur}</span>
              </span>
            );
          }
          case 'lien':
            return (
              <span key={i} className="nom-carte">
                «{NBSP}{(m as { libelle: string }).libelle}{NBSP}»
              </span>
            );
          case 'valeur':
            return (
              <span key={i} className="nom-carte">
                «{NBSP}{(m as { v: string }).v}{NBSP}»
              </span>
            );
          case 'chiffre': {
            const c = chiffres.get((m as { c: string }).c);
            if (!c) return <span key={i}>(chiffre inconnu)</span>;
            if (!basculer || !ouverts) return <span key={i} className="chiffre">{c.texte}</span>;
            const ouvert = ouverts.has(c.id);
            return (
              <button
                key={i}
                type="button"
                className="chiffre"
                aria-expanded={ouvert}
                aria-controls={ouvert ? `${base}-${c.id}` : undefined}
                title="Voir d’où vient ce chiffre"
                onClick={() => basculer(c.id)}
              >
                {c.texte}
              </button>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}

/**
 * Le calcul d'un chiffre. La valeur est le texte que la console a écrit (« 1 118 466 € », « de 5 941 644 à 12 206 575 € ») :
 * jamais refaite ici, sinon le panneau dirait « 1 118 465,753 € » sous une phrase qui dit « 1 118 466 € ». Pour un chiffre du
 * moteur : la fonction, les paramètres et les hypothèses de son calcul (dont celle qui dit que fréquences, durées et coûts
 * de rattrapage ne sont pas calibrés sur des sinistres réels), puis la recette que le contrôle de la console refait.
 */
function CalculChiffre({
  id,
  chiffre,
  operations,
  sources,
  surFermer,
}: {
  id: string;
  chiffre: ChiffreConsole;
  operations: OperationConsole[];
  sources: SourceConsole[];
  surFermer: () => void;
}) {
  const op = chiffre.operation ? operations.find((o) => o.id === chiffre.operation) : undefined;
  const source = sourceDuCalcul(chiffre, operations, sources);
  const recette = recetteCalcul(chiffre);
  return (
    <div className="calcul-chiffre" id={id} role="group" aria-label={`Calcul : ${chiffre.libelle}`} data-testid="calcul-chiffre">
      <p>
        <b>{majuscule(chiffre.libelle)}</b> : <span className="num">{chiffre.texte}</span>
      </p>
      <p className="note">{LIBELLE_ORIGINE[chiffre.origine] ?? chiffre.origine}</p>
      {chiffre.suppose && (
        <p className="note">
          <b>Supposé par la console</b> : votre question ne le dit pas.
        </p>
      )}
      {source?.calcul && <DetailCalcul calcul={source.calcul} />}
      {recette ? (
        <p className="note">
          Refait par le contrôle de la console : <span className="num">{recette}</span>
        </p>
      ) : chiffre.origine === 'moteur' && !source ? (
        <p className="note">Le détail du calcul est dans l’opération ci-dessous.</p>
      ) : null}
      {op && (
        <p className="note">
          Opération <span className="num">{op.id}</span> ({op.operation})
          {!source && op.hypotheses && op.hypotheses.length > 0 ? ` : ${op.hypotheses.join(' ; ')}` : ''}
        </p>
      )}
      <button type="button" className="btn petit" onClick={surFermer}>
        Replier le calcul
      </button>
      <LienRetour depuis="chiffre-console" />
    </div>
  );
}

/** Ce que dit une source « calcul » : la fonction du moteur, ses paramètres et ses hypothèses (texte brut). */
function DetailCalcul({ calcul }: { calcul: NonNullable<SourceConsole['calcul']> }) {
  const hypotheses = calcul.hypotheses ?? [];
  return (
    <div className="note detail-calcul">
      <p>
        Fonction <span className="num">{calcul.fonction}</span>
      </p>
      {Object.entries(calcul.parametres ?? {}).map(([k, v]) => (
        <p key={k} className="num">
          {k} = {ligneTexte(v)}
        </p>
      ))}
      {hypotheses.length > 0 && (
        <>
          <p>
            <b>Hypothèses du calcul</b> :
          </p>
          <ul>
            {hypotheses.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- sources et opérations

const LIBELLE_TYPE_SOURCE: Record<SourceConsole['type'], string> = {
  objet: 'Objet de la carte',
  attribut: 'Attribut',
  lien: 'Lien entre objets',
  constat: 'Constat',
  contradiction: 'Point à confirmer (contradiction entre pièces)',
  calcul: 'Calcul du moteur',
  recherche: 'Recherche dans la carte',
  entreprise: 'Entreprise et organisation',
};

function SourceBloc({ source: s }: { source: SourceConsole }) {
  return (
    <div className="source-bloc" data-testid="source">
      <div className="ligne">
        <Etiquette ton="neutre">{LIBELLE_TYPE_SOURCE[s.type] ?? 'Source'}</Etiquette>
        {s.libelle &&
          (s.objet ? (
            <a className="nom-carte lien" href={lienVers('carte', { objet: s.objet })}>
              «{NBSP}{s.libelle}{NBSP}»
            </a>
          ) : (
            <span className="nom-carte">«{NBSP}{s.libelle}{NBSP}»</span>
          ))}
        {s.attribut && <span className="note">attribut : {s.attribut}</span>}
      </div>
      {(s.statut || s.methode || s.confiance != null || s.valide_par) && (
        <p className="note">
          {s.statut ? (LIBELLE_STATUT_FAIT[s.statut] ?? s.statut) : null}
          {s.confiance != null ? ` · confiance ${pourcent(s.confiance)}` : ''}
          {s.methode ? ` · lu par ${s.lu_par_modele || s.methode === 'modele' ? 'le modèle' : 'des règles'}` : ''}
          {s.valide_par ? ` · validé par ${s.valide_par}` : ''}
        </p>
      )}
      {s.calcul && <DetailCalcul calcul={s.calcul} />}
      {s.criteres && (
        <p className="note num">
          {Object.entries(s.criteres)
            .map(([k, v]) => `${k} = ${ligneTexte(v)}`)
            .join(' ; ')}
        </p>
      )}
      {(s.citations?.length ?? 0) > 0 ? (
        <Citations citations={(s.citations ?? []).map((c) => ({ source: c.piece, localisateur: c.localisateur, extrait: c.extrait }))} />
      ) : !s.citee ? (
        <p className="note">(non cité : déduit de la carte)</p>
      ) : null}
    </div>
  );
}

function Operation({ op }: { op: OperationConsole }) {
  const parametres = Object.entries(op.parametres ?? {});
  return (
    <div className="operation" data-testid="operation">
      <p>
        <b className="num">{op.id}</b> · {op.operation} : {op.nombre ?? 0} résultat{(op.nombre ?? 0) > 1 ? 's' : ''}
        {op.tronque ? ' (tronqué)' : ''}
        {op.erreur ? ` · erreur : ${op.erreur}` : ''}
      </p>
      {parametres.length > 0 && (
        <p className="note num">{parametres.map(([k, v]) => `${k} = ${ligneTexte(v)}`).join(' ; ')}</p>
      )}
      {(op.enonces ?? []).length > 0 && (
        <ul className="liste-simple">
          {(op.enonces ?? []).map((e) => (
            <li key={e.id}>
              <span className="num">{e.id}</span> {e.texte}
              {e.note ? ` ${e.note}` : ''}
            </li>
          ))}
        </ul>
      )}
      {(op.hypotheses ?? []).map((h, i) => (
        <p key={i} className="note">
          Hypothèse : {h}
        </p>
      ))}
    </div>
  );
}
